export interface ShopifyLineItem {
  id: number
  sku?: string
  product_id?: number
  variant_id?: number
  title?: string
  variant_title?: string
  quantity?: number
  price?: string // Shopify uses string for prices
  total_discount?: string
  properties?: { name: string; value: string }[]
}

export interface ShopifyOrder {
  id: number
  name?: string
  email?: string
  created_at?: string
  updated_at?: string
  currency?: string
  current_total_price?: string
  total_price?: string
  current_subtotal_price?: string
  subtotal_price?: string
  total_discounts?: string
  total_tax?: string
  financial_status?: string // 'paid', 'pending', ...
  fulfillment_status?: string
  order_status_url?: string
  tags?: string
  note?: string
  discount_codes?: { code: string; amount: string; type: string }[]
  shipping_address?: {
    zip?: string
    city?: string
    name?: string
    phone?: string | null
    company?: string | null
    country?: string
    address1?: string
    address2?: string | null
    latitude?: number
    province?: string | null
    last_name?: string
    longitude?: number
    first_name?: string
    country_code?: string | null
    province_code?: string | null
  }
  customer?: {
    id?: number
    email?: string
    phone?: string
    first_name?: string
    last_name?: string
    default_address?: {
      id?: number
      zip?: string
      city?: string
      name?: string | null
      phone?: string | null
      company?: string | null
      country?: string | null
      default?: boolean
      address1?: string
      address2?: string | null
      province?: string | null
      last_name?: string
      first_name?: string
      customer_id?: number
      country_code?: string
      country_name?: string
      province_code?: string | null
    }
  }
  billing_address?: {
    zip?: string
    city?: string
    name?: string
    phone?: string | null
    company?: string | null
    country?: string
    address1?: string
    address2?: string | null
    latitude?: number
    province?: string | null
    last_name?: string
    longitude?: number
    first_name?: string
    country_code?: string | null
    province_code?: string | null
  }
  total_shipping_price?: string          // Product Economics: customer-paid shipping
  source_name?: string
  processed_at?: string
  line_items?: ShopifyLineItem[]
  /** Product Economics: per-line-item refund data extracted from GraphQL refunds */
  _refund_line_items?: Array<{
    line_item_id: number
    quantity: number
    subtotal: number
  }>
  /** Product Economics: number of refund events on this order (from gqlOrder.refunds.length) */
  _refund_event_count?: number
}

/** Row shape for shopify_order_line_items table */
export interface LineItemRow {
  integration_id: string
  shopify_order_id: string
  line_item_id: number
  product_id: number | null
  variant_id: number | null
  title: string
  variant_title: string | null
  sku: string | null
  quantity: number
  unit_price: number
  total_discount: number
  line_total: number
  properties: { name: string; value: string }[] | null
}

/** Product option for multi-select filter */
export interface ProductOption {
  product_id: number
  title: string
  sku?: string | null
  variant_title?: string | null
  variant_count: number
  order_count: number
}

/** Format subtitle line: SKU + variant_title */
export function productSubtitle(p: ProductOption): string {
  return [p.sku ? `SKU: ${p.sku}` : null, p.variant_title].filter(Boolean).join(" · ")
}

/**
 * Product Family — view-layer projection of a `product_family_rules` row plus
 * the products it currently resolves to.
 *
 *   id          — rule.id (uuid)
 *   name        — rule.name (display label, e.g. "Bags")
 *   family_key  — rule.family_key (canonical key, e.g. "bags")
 *   products    — hydrated ProductOption[], aggregated from
 *                 shopify_order_line_items rows whose family_key matches the
 *                 rule (or whose product_id is in the rule's product_id_list).
 */
export interface ProductFamily {
  id: string
  name: string
  family_key: string
  products: ProductOption[]
}

/**
 * Lightweight projection used by the family picker. Carries the rule
 * identity + an aggregate COUNT only — no per-product hydration. Backed by
 * `loadProductFamiliesLight` (single RPC call: product_families_light_counts).
 *
 * Callers that need the full ProductOption[] (overlap detection, persisted
 * AnalysisItem, Bundle Builder slot) hydrate one family on demand via
 * GET /api/integrations/shopify/product-families/[id] (`hydrateFamily`
 * action on `useProductFamilies`). Don't mutate ProductFamilyLight back into
 * ProductFamily by pretending products = [] — surface the type difference
 * so the call site is clearly «light list» vs «full hydrate».
 */
export interface ProductFamilyLight {
  id: string
  name: string
  family_key: string
  product_count: number
}
